import React, { useState, useEffect } from 'react';
import { StudentDetail } from '../types';
import { studentService } from '../services/studentService';

interface StudentDetailsPageProps {
  onBack: () => void;
  onStudentFound: (student: StudentDetail) => void;
}

interface ClassOption {
  class_code: string;
  class_name: string;
}

const StudentDetailsPage: React.FC<StudentDetailsPageProps> = ({
  onBack,
  onStudentFound,
}) => {
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [studentSearchQuery, setStudentSearchQuery] = useState('');

  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [sections, setSections] = useState<string[]>([]);
  const [students, setStudents] = useState<StudentDetail[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<StudentDetail[]>([]);

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [loadingSections, setLoadingSections] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);

  // Load classes on component mount
  useEffect(() => {
    const loadClasses = async () => {
      setLoadingClasses(true);
      try {
        const classesData = await studentService.getAllClasses();
        setClasses(classesData);
      } catch (err) {
        setError('Failed to load classes');
        console.error('Error loading classes:', err);
      } finally {
        setLoadingClasses(false);
      }
    };

    loadClasses();
  }, []);

  // Load sections when class changes
  useEffect(() => {
    if (selectedClass) {
      const loadSections = async () => {
        setLoadingSections(true);
        setSections([]);
        setSelectedSection('');
        setStudents([]);
        setFilteredStudents([]);
        setSelectedStudent('');
        setStudentSearchQuery('');

        try {
          const sectionsData = await studentService.getSectionsByClass(selectedClass);
          setSections(sectionsData);
        } catch (err) {
          setError('Failed to load sections');
          console.error('Error loading sections:', err);
        } finally {
          setLoadingSections(false);
        }
      };
      loadSections();
    } else {
      setSections([]);
      setSelectedSection('');
      setStudents([]);
      setFilteredStudents([]);
      setSelectedStudent('');
      setStudentSearchQuery('');
    }
  }, [selectedClass]);

  // Load students when class and section are selected
  useEffect(() => {
    if (selectedClass && selectedSection) {
      const loadStudents = async () => {
        setLoadingStudents(true);
        setStudents([]);
        setFilteredStudents([]);
        setSelectedStudent('');
        setStudentSearchQuery('');

        try {
          const studentsData = await studentService.getStudentsByClass(selectedClass);
          // Filter by section
          const sectionStudents = studentsData.filter(
            s => s.class === selectedSection
          );
          setStudents(sectionStudents);
          setFilteredStudents(sectionStudents);
        } catch (err) {
          setError('Failed to load students');
          console.error('Error loading students:', err);
        } finally {
          setLoadingStudents(false);
        }
      };
      loadStudents();
    } else {
      setStudents([]);
      setFilteredStudents([]);
      setSelectedStudent('');
      setStudentSearchQuery('');
    }
  }, [selectedClass, selectedSection]);

  // Filter students based on search query
  useEffect(() => {
    if (studentSearchQuery.trim() === '') {
      setFilteredStudents(students);
    } else {
      const filtered = students.filter(student =>
        student.studentName.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
        student.studentCode.toLowerCase().includes(studentSearchQuery.toLowerCase())
      );
      setFilteredStudents(filtered);
    }
  }, [studentSearchQuery, students]);

  const handleStudentSelect = (student: StudentDetail) => {
    console.log('Selected student:', student);
    setSelectedStudent(student.studentCode);
    setStudentSearchQuery(student.studentName);
    setShowStudentDropdown(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!selectedClass || !selectedSection || !selectedStudent) {
      setError('Please select all fields');
      return;
    }

    setIsLoading(true);
    try {
      const student = await studentService.getStudentByCode(selectedStudent);
      if (student) {
        onStudentFound(student);
      } else {
        setError('Student not found. Please check your selections.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 md:pb-32">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white p-6 shadow-lg">
        <div className="w-full px-4 md:max-w-6xl md:mx-auto">
          <h1 className="text-3xl font-bold">Search by Student Details</h1>
          <p className="text-primary-100">Select class, section, and student name</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-2 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <form onSubmit={handleSubmit}>
            {/* Class */}
            <div className="mb-6">
              <label className="block text-gray-700 font-bold mb-2">
                Class <span className="text-red-600">*</span>
              </label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                disabled={loadingClasses || isLoading}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary-600 text-lg transition-colors duration-200 disabled:bg-gray-100"
              >
                <option value="">Select Class</option>
                {classes.map((cls) => (
                  <option key={cls.class_code} value={cls.class_code}>
                    {cls.class_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Section */}
            <div className="mb-6">
              <label className="block text-gray-700 font-bold mb-2">
                Section <span className="text-red-600">*</span>
              </label>
              <select
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                disabled={loadingSections || isLoading || !selectedClass}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary-600 text-lg transition-colors duration-200 disabled:bg-gray-100"
              >
                <option value="">Select Section</option>
                {sections.map((section) => (
                  <option key={section} value={section}>
                    {section}
                  </option>
                ))}
              </select>
            </div>

            {/* Student Name - Searchable Dropdown */}
            <div className="mb-6 relative">
              <label className="block text-gray-700 font-bold mb-2">
                Student Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={studentSearchQuery}
                onChange={(e) => {
                  setStudentSearchQuery(e.target.value);
                  setShowStudentDropdown(true);
                }}
                onFocus={() => setShowStudentDropdown(true)}
                disabled={loadingStudents || isLoading || !selectedSection}
                placeholder="Search student name..."
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary-600 text-lg transition-colors duration-200 disabled:bg-gray-100"
              />
              {/* Dropdown List */}
              {showStudentDropdown && filteredStudents.length > 0 && studentSearchQuery && (
                <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredStudents.map((student) => (
                    <div
                      key={student.studentCode}
                      onClick={() => handleStudentSelect(student)}
                      className="px-4 py-3 hover:bg-primary-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-semibold text-gray-800">{student.studentName}</div>
                      <div className="text-sm text-gray-500">Code: {student.studentCode}</div>
                    </div>
                  ))}
                </div>
              )}
              {loadingStudents && (
                <p className="text-sm text-blue-600 mt-1">Loading students...</p>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 bg-red-50 border-l-4 border-red-600 p-4 rounded">
                <p className="text-red-700 font-semibold">{error}</p>
              </div>
            )}

            {/* Search Button */}
            <div>
              <button
                type="submit"
                disabled={
                  isLoading ||
                  !selectedClass ||
                  !selectedSection ||
                  !selectedStudent
                }
                className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white font-bold py-3 px-4 rounded-lg transition-colors duration-200"
              >
                {isLoading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Fixed Bottom Action Bar (Desktop only, floating on mobile) */}
      <div className="md:fixed md:bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 shadow-2xl z-50 mt-8 md:mt-0">
        <div className="max-w-7xl mx-auto px-2 py-4">
          <div className="flex gap-4 max-w-2xl mx-auto">
            <button
              onClick={onBack}
              className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-4 px-6 rounded-lg transition-colors duration-200 text-lg"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDetailsPage;
